import type { LeadPlatformPerformance, SalesSubject } from '@/client/types/sales';
import { daysAgo, hoursAgo } from './fixtures';

/**
 * Mock platform performance for Sales lead detail.
 * Intended later: compose user listings + org memberships + payments/promos by uid/oid.
 */
const BY_UID: Record<number, Omit<LeadPlatformPerformance, 'subjectId' | 'totals'>> = {
  55102: {
    uid: 55102,
    linkedAt: daysAgo(40),
    personal: { listingCount: 1, lifetimePaidGhs: 120 },
    orgs: [
      {
        oid: 2201,
        name: 'Owusu Homes Accra',
        role: 'owner',
        listingCount: 8,
        activeListings: 6,
        lifetimePaidGhs: 4_800,
        promoSpendGhs: 3_200,
        lastActiveAt: hoursAgo(6),
      },
      {
        oid: 2208,
        name: 'East Legon Lets',
        role: 'admin',
        listingCount: 3,
        activeListings: 2,
        lifetimePaidGhs: 1_150,
        promoSpendGhs: 900,
        lastActiveAt: daysAgo(2),
      },
      {
        oid: 2310,
        name: 'Tema Weekends',
        role: 'member',
        listingCount: 0,
        activeListings: 0,
        lifetimePaidGhs: 0,
        promoSpendGhs: 0,
        lastActiveAt: daysAgo(18),
      },
    ],
  },
  88200: {
    uid: 88200,
    linkedAt: daysAgo(3),
    personal: { listingCount: 0, lifetimePaidGhs: 0 },
    orgs: [
      {
        oid: 3102,
        name: 'Sarpong Family Rentals',
        role: 'owner',
        listingCount: 0,
        activeListings: 0,
        lifetimePaidGhs: 0,
        promoSpendGhs: 0,
        lastActiveAt: daysAgo(2),
      },
    ],
  },
  44021: {
    uid: 44021,
    linkedAt: daysAgo(12),
    personal: { listingCount: 0, lifetimePaidGhs: 0 },
    orgs: [],
  },
  77021: {
    uid: 77021,
    linkedAt: daysAgo(35),
    personal: { listingCount: 2, lifetimePaidGhs: 0 },
    orgs: [
      {
        oid: 4401,
        name: 'Serwaa Motors Kumasi',
        role: 'owner',
        listingCount: 4,
        activeListings: 3,
        lifetimePaidGhs: 0,
        promoSpendGhs: 0,
        lastActiveAt: hoursAgo(10),
      },
    ],
  },
  91001: {
    uid: 91001,
    linkedAt: daysAgo(45),
    personal: { listingCount: 2, lifetimePaidGhs: 450 },
    orgs: [
      {
        oid: 5102,
        name: 'Asiedu Homes East Legon',
        role: 'owner',
        listingCount: 7,
        activeListings: 6,
        lifetimePaidGhs: 3_800,
        promoSpendGhs: 2_400,
        lastActiveAt: hoursAgo(3),
      },
    ],
  },
};

function withTotals(
  subjectId: string,
  raw: Omit<LeadPlatformPerformance, 'subjectId' | 'totals'>,
): LeadPlatformPerformance {
  const orgListings = raw.orgs.reduce((s, o) => s + o.listingCount, 0);
  const orgPaid = raw.orgs.reduce((s, o) => s + o.lifetimePaidGhs, 0);
  const orgPromo = raw.orgs.reduce((s, o) => s + o.promoSpendGhs, 0);
  return {
    subjectId,
    ...raw,
    totals: {
      listingCount: raw.personal.listingCount + orgListings,
      lifetimePaidGhs: raw.personal.lifetimePaidGhs + orgPaid,
      promoSpendGhs: orgPromo,
      orgCount: raw.orgs.length,
    },
  };
}

/** Resolve mock performance for a subject that has a FeelyFeely uid. */
export function leadPerformanceFor(subject: SalesSubject): LeadPlatformPerformance | null {
  const uid = subject.platform?.uid;
  if (uid == null) return null;
  const raw = BY_UID[uid];
  if (raw) return withTotals(subject.id, raw);

  // Synthetic fallback from flat PlatformSnapshot (single oid if present).
  const oid = subject.platform?.oid;
  return withTotals(subject.id, {
    uid,
    linkedAt: subject.platform?.lastActiveAt,
    personal: {
      listingCount: oid != null ? 0 : subject.platform?.listingCount ?? 0,
      lifetimePaidGhs: oid != null ? 0 : subject.platform?.lifetimePaidGhs ?? 0,
    },
    orgs:
      oid != null
        ? [
            {
              oid,
              name: `Organization ${oid}`,
              role: 'owner',
              listingCount: subject.platform?.listingCount ?? 0,
              activeListings: subject.platform?.listingCount ?? 0,
              lifetimePaidGhs: subject.platform?.lifetimePaidGhs ?? 0,
              promoSpendGhs: 0,
              lastActiveAt: subject.platform?.lastActiveAt,
            },
          ]
        : [],
  });
}
